import { Box, Typography } from '@mui/material';
import { type ReactNode, useState } from 'react';

import { tabLabelWithCount } from '~/components/common/TabCount';
import ToggleButtonGroup from '~/components/common/ToggleButtonGroup';

interface SectionTab {
  label: ReactNode;
  /** Distinguishes the tab in `aria` and as a React key; the label is a node. */
  value: string;
  /** A function, not an element: only the selected tab's body is built. */
  render: () => ReactNode;
}

/** `Tickets` with a `12` chip beside it — outlined rather than filled, so a subtab's number doesn't compete with the page strip's above it. */
export const withCount = (label: string, count: number | undefined): ReactNode =>
  tabLabelWithCount(label, count, 'outlined');

interface SectionTabsProps {
  tabs: SectionTab[];
  ariaLabel: string;
  /**
   * Makes the control controlled, for a section whose selection is not its own to keep —
   * the property page's Spending tab holds its class in `invoice_kind` so a link can open
   * it on Proposals. Pass both or neither.
   */
  value?: string;
  onChange?: (value: string) => void;
  /** Sits beside the tabs to say what the whole set of views is scoped to. */
  helperText?: ReactNode;
  /** Pinned to the far end of the tab row — for an action on the section rather than on a view. */
  actions?: ReactNode;
}

/**
 * Switch between views inside a page tab — the elevators an invoice serviced and the
 * tickets raised against them, or an elevator's open violations and its resolved ones.
 *
 * Values come from each tab's `value`, not its label: the labels carry count chips that
 * arrive after the first render. Only the selected tab's body is rendered, so a view's
 * queries don't run until someone asks for it.
 */
export function SectionTabs({
  tabs,
  ariaLabel,
  value,
  onChange,
  helperText,
  actions,
}: SectionTabsProps) {
  // Always called: a hook can't be conditional. Ignored while `value` is given.
  const [selected, setSelected] = useState(tabs[0].value);
  const current = value ?? selected;
  const active = tabs.find((tab) => tab.value === current) ?? tabs[0];

  return (
    <Box minWidth={0}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          aria-label={ariaLabel}
          size="small"
          activeView={active.value}
          viewList={tabs.map((tab) => ({ value: tab.value, title: tab.label }))}
          // Clicking the selected button deselects it in an exclusive group, which
          // would leave the section with no view; hold the current one instead.
          handleChange={(_event, next) => {
            if (next === null) return;
            if (onChange) {
              onChange(next);
            } else {
              setSelected(next);
            }
          }}
        />
        {helperText ? (
          <Typography variant="caption" color="text.secondary">
            {helperText}
          </Typography>
        ) : null}
        {actions ? <Box sx={{ ml: 'auto' }}>{actions}</Box> : null}
      </Box>
      {active.render()}
    </Box>
  );
}
